// Client helper that POSTs to the /api/chat proxy.
//
// The proxy first tries the primary chat endpoint (HF Space) with
// `{ message, history }` and falls back to OpenAI if that fails.
//
// `caseId` lets us prepend the case master-context markdown to the system
// prompt on the OpenAI fallback path (the primary endpoint is expected to
// have its own retrieval / system prompt baked in).
//
// Streaming: pass `onChunk` to receive incremental deltas. The Promise
// still resolves with the final aggregated result. The proxy emits NDJSON
// when streaming is requested:
//   {"type":"meta","source":"primary"|"openai","sources":[…],"primary_error":"…"}
//   {"type":"delta","text":"…"}
//   {"type":"done"} | {"type":"error","error":"…"}

import { getCaseContext } from './cases';

export async function chatCall({
  message,
  history = [],
  caseId,
  system,
  max_tokens,
  signal,
  onChunk,
}) {
  const ctx = caseId ? getCaseContext(caseId) : null;
  const systemParts = [];
  if (ctx) systemParts.push(ctx);
  if (system) systemParts.push(system);
  const finalSystem = systemParts.join('\n\n---\n\n') || undefined;

  const stream = !!onChunk;
  const r = await fetch('/api/chat', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      message,
      history,
      caseId,
      system: finalSystem,
      max_tokens,
      stream,
    }),
    signal,
  });
  if (!r.ok) {
    const txt = await r.text().catch(() => '');
    throw new Error(`/api/chat ${r.status}: ${txt || r.statusText}`);
  }

  if (!stream) {
    const data = await r.json();
    if (data.error) throw new Error(data.error);
    return {
      content: data.content || '',
      source: data.source || 'primary',
      sources: data.sources,
      primary_error: data.primary_error,
    };
  }

  const reader = r.body && r.body.getReader ? r.body.getReader() : null;
  if (!reader) throw new Error('/api/chat returned no stream body');
  const decoder = new TextDecoder();
  let buffer = '';
  let full = '';
  let result = { content: '', source: 'primary' };
  let errored = null;

  const handleEvent = (e) => {
    if (e.type === 'meta') {
      result = {
        ...result,
        source: e.source,
        sources: e.sources,
        primary_error: e.primary_error,
      };
      onChunk({
        delta: '',
        full,
        source: e.source,
        sources: e.sources,
        primary_error: e.primary_error,
      });
    } else if (e.type === 'delta') {
      full += e.text;
      onChunk({
        delta: e.text,
        full,
        source: result.source,
        sources: result.sources,
      });
    } else if (e.type === 'error') {
      errored = e.error;
    }
  };

  const handleLine = (line) => {
    if (!line) return;
    try {
      handleEvent(JSON.parse(line));
    } catch {
      // tolerate malformed lines
    }
  };

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let nl;
    while ((nl = buffer.indexOf('\n')) !== -1) {
      handleLine(buffer.slice(0, nl).trim());
      buffer = buffer.slice(nl + 1);
    }
  }
  if (buffer.trim()) handleLine(buffer.trim());

  if (errored) throw new Error(errored);
  result.content = full;
  return result;
}
